import { takeEvery, buffers, delay } from 'redux-saga'
import { put, call, spawn, take, select, join, actionChannel, race, flush } from 'redux-saga/effects'
import { applyProjectsAction, expandProjectAction } from 'actions/projectActions'
import { notifyHoneybadgerAction, projectExpandedAction, updateCurrentEntityAction } from 'actions/applicationActions'
import { getAndApplyCameraData } from 'sagas/getAndApplyCameraDataSaga'
import { SETUP_EXPORT, FLOOR_PLAN_READY, CAMERA_MODEL_IMAGE_READY, SCREENSHOT_CAPTURED, FOV_PREVIEW_READY,
  NETWORK_CLOSET_LIST_READY, SET_NETWORK_CLOSET_LIST_PAGES } from 'actions/exportActionTypes'
import { PROJECT_EXPANDED } from 'actions/applicationActionTypes'
import { floorPlansLoadedSelector, startExportProcessSelector, setupCameraSelector,
  setupNetworkClosetSelector, fovPreviewSelector, exportDoDevicesSelector } from 'selectors'
import { zoomToAllDevices } from 'sagas/zoomToAllDevicesSaga'
import { setCameraToActiveAction, NEXT_CAMERA } from 'actions/cameraActions'
import { updatePresentationValuesAction } from 'actions/presentationActions'
import { zoomToSingleCameraAction, zoomToNetworkClosetAction, zoomToWiredDeviceAction, zoomToDoorControllerAction } from 'actions/mapActions'
import { updateExportTypeAndResizeMapAction, clearFovPreviewReadyAction } from 'actions/exportActions'
import { updateNetworkClosetAction } from 'actions/entityActions'
import path from 'ramda/src/path'
import waitForTilesToLoad from 'helpers/map/waitForTilesToLoad'
import getScreenshotClipFromBounds from 'helpers/map/getScreenshotClipFromBounds'
import mapTiles from 'helpers/map/mapTiles'
import getImageCount from 'helpers/map/getImageCount'
import { updateUiAction } from 'actions/uiActions'

function * waitForFloorPlans(floorPlans) {
  if (floorPlans.length === 0) return

  while (true) {
    yield take(global.floorPlanReadyChannel)
    const { floorPlansLoaded, floorPlans } = yield select(floorPlansLoadedSelector)
    if (floorPlans.length === floorPlansLoaded) {
      console.warn('DEBUG', 'Floor plans have loaded')
      break
    } else console.warn('DEBUG', `Floor plans not ready, waiting on ${floorPlans.length - floorPlansLoaded} to load`)
  }
}

function * waitForCameraModelImage(cameras) {
  if (!path(['model', 'imageUrl'], cameras.find(c => c.active))) return
  yield take(CAMERA_MODEL_IMAGE_READY)
}

function * setupCamera(camera, checkMaxZoom) {
  const { currentEntity: { type, id }, currentCamera } = yield select(setupCameraSelector)
  if (camera.id !== currentCamera.id) {
    yield put(setCameraToActiveAction(camera.id))
    yield put(updatePresentationValuesAction())
  } else if (type !== 'camera' || id !== camera.id) {
    yield put(updateCurrentEntityAction({ type: 'camera', id: camera.id }))
  }
  yield put.resolve(zoomToSingleCameraAction(camera, checkMaxZoom))
}

function * setupNetworkCloset(networkCloset, checkMaxZoom, clearChannel) {
  if (clearChannel) yield flush(global.networkClosetListChannel)

  yield put(updateCurrentEntityAction({ type: 'networkCloset', id: networkCloset.id }))
  yield put.resolve(zoomToNetworkClosetAction(networkCloset, checkMaxZoom))
}

function * setupWiredDevice(wiredDevice, checkMaxZoom) {
  yield put(updateCurrentEntityAction({ type: 'wiredDevice', id: wiredDevice.id }))
  yield put.resolve(zoomToWiredDeviceAction(wiredDevice, checkMaxZoom))
}

function * setupDoorController(doorController, checkMaxZoom) {
  yield put(updateCurrentEntityAction({ type: 'doorController', id: doorController.id }))
  yield put.resolve(zoomToDoorControllerAction(doorController, checkMaxZoom))
}

function * setupDoor(door) {
  yield put(updateCurrentEntityAction({ type: 'door', id: door.id }))
  yield put.resolve(zoomToWiredDeviceAction(door))
}

function * waitForSingleCameraImage(camera) {
  if (!path(['model', 'imageUrl'], camera)) return
  yield take(CAMERA_MODEL_IMAGE_READY)
}

function * waitForFovPreview() {
  const { currentEntity: { type }, fovReady } = yield select(fovPreviewSelector)
  if (type === 'camera' && !fovReady) {
    yield take(FOV_PREVIEW_READY)
  }
}

function * takeScreenshot(opts, stepThrough) {
  window.handlers.takeScreenshot(opts)
  if (!stepThrough) yield take(SCREENSHOT_CAPTURED)
}

function exit() {
  window.handlers.exit()
}

function * waitForTilesToLoadRace(map) {
  if (map.mapType === 'blank') return
  const { timeOut } = yield race({
    tiles: call(waitForTilesToLoad, map.mapType),
    timeOut: call(delay, 2500)
  })

  if (timeOut) {
    console.warn('DEBUG', 'Tiles not ready within 2500ms, moving on...')
  }
}

function waitForMapOnce(map) {
  return new Promise(resolve => {
    return google.maps.event.addListenerOnce(map, 'idle', _ => {
      console.warn('DEBUG', 'Map idle')
      resolve()
    })
  })
}

function * waitForMapOnceRace(map, timeBeforeCameraIsReady) {
  const { timeOut } = yield race({
    tiles: call(waitForMapOnce, map.ref),
    timeOut: call(delay, 2000)
  })

  if (timeOut) console.warn('DEBUG', 'Map not ready in 2s, moving on')
  else console.warn('DEBUG', `Took ${Date.now() - timeBeforeCameraIsReady}ms for map to be ready`)
}

function waitForTileCount(ref, mapType) {
  const overlay = new google.maps.OverlayView()
  overlay.setMap(ref)
  return new Promise(resolve => {
    global.tileInterval = setInterval(_ => {
      const images = mapTiles(mapType)
      console.warn('DEBUG', `Counted ${images.length} images`)
      if (images.length !== 0 && images.length === getImageCount(overlay.getPanes().mapPane)) {
        resolve()
      }
    }, 10)
  })
}

function * waitForTileCountRace(map) {
  if (map.mapType === 'blank') return
  const time = Date.now()
  const { timeOut } = yield race({
    tiles: call(waitForTileCount, map.ref, map.mapType),
    timeOut: call(delay, 2000)
  })

  clearInterval(global.tileInterval)

  if (timeOut) console.warn('DEBUG', 'Tiles did not match in 2s, moving on')
  else console.warn('DEBUG', `Took ${Date.now() - time}ms for tile count to match`)
}

function waitForTilesloaded(map) {
  return new Promise(resolve => {
    return google.maps.event.addListenerOnce(map, 'tilesloaded', _ => {
      console.warn('DEBUG', 'Map tilesloaded')
      resolve()
    })
  })
}

function * waitForTilesloadedRace(map) {
  const time = Date.now()
  const { timeOut } = yield race({
    tiles: call(waitForTilesloaded, map.ref),
    timeOut: call(delay, 2000)
  })

  if (timeOut) console.warn('DEBUG', 'Tiles did not load in 2s, moving on')
  else console.warn('DEBUG', `Took ${Date.now() - time}ms for tiles to load`)
}

function * doCameras(cameras, stepThrough, cameraIds, map, lambdaNumber) {
  const cams = cameras.filter(c => cameraIds.includes(c.id))
  console.warn('DEBUG', `Starting to process cameras: ${cameraIds}`)
  for (let [index, id] of cameraIds.entries()) {
    const timeBeforeCameraIsReady = Date.now()

    const camera = cams.find(c => c.id === id)
    const a = yield spawn(waitForMapOnceRace, map, timeBeforeCameraIsReady)
    yield call(setupCamera, camera, map.mapType !== 'blank' && index === 0)
    // since there is no tile wait that we can do, we wait a small amount of
    // time per camera when map type is blank
    // https://github.com/ipvideomarketinfo/calculator-v3/issues/599
    if (map.mapType === 'blank') yield call(delay, 1000)
    yield join(a)

    yield call(waitForTileCountRace, map)

    const b = yield spawn(waitForTilesToLoadRace, map)
    const c = yield spawn(waitForSingleCameraImage, camera)
    const d = yield spawn(waitForFovPreview)
    yield join(b, c, d)

    console.warn('DEBUG', `Ready to take snapshot, took ${Date.now() - timeBeforeCameraIsReady}ms`)
    if (index === 0) yield call(delay, 500)

    const snapshotIndex = (lambdaNumber - 1) * 5 + index
    const screenshotOpts = {
      fileName: `camera_${snapshotIndex}`,
      name: camera.name,
      index: snapshotIndex
    }
    yield call(takeScreenshot, screenshotOpts, stepThrough)
    yield put.resolve(clearFovPreviewReadyAction())
    if (stepThrough) yield take(NEXT_CAMERA)
  }
}

function * doNetworkCloset(networkCloset, index, stepThrough, map, lambdaNumber, clearChannel) {
  const timeBeforeDeviceIsReady = Date.now()

  yield call(setupNetworkCloset, networkCloset, map.mapType !== 'blank' && index === 0, clearChannel)

  yield call(waitForMapOnceRace, map, timeBeforeDeviceIsReady)
  yield call(waitForTileCountRace, map)
  yield call(waitForTilesToLoadRace, map)

  console.warn('DEBUG', `Ready to set device lists, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

  if (index === 0) yield call(delay, 500)

  const { pages } = yield take(global.networkClosetListChannel)

  if (pages.length > 0) {
    for (let pageIdx = 0; pageIdx < pages.length; pageIdx += 1) {
      yield put.resolve(updateNetworkClosetAction(networkCloset.id, { listStartIdx: pages[pageIdx][0], listEndIdx: pages[pageIdx][1] }))
      yield take(NETWORK_CLOSET_LIST_READY)

      const snapshotIndex = (lambdaNumber - 1) * 5 + index
      const deviceRange = `${pages[pageIdx][0] + 1}–${pages[pageIdx][1]} of ${pages[pages.length - 1][1]}`

      console.warn('DEBUG', `Ready to take page ${pageIdx + 1} of ${pages.length} screenshot, took ${Date.now() - timeBeforeDeviceIsReady}ms`)
      const screenshotOpts = {
        fileName: `network_closet_${snapshotIndex}_${pageIdx + 1}`,
        name: `${networkCloset.name} (Devices ${deviceRange})`,
        index: snapshotIndex + pageIdx / pages.length,
        skipProgress: pageIdx !== 0
      }
      yield call(takeScreenshot, screenshotOpts, stepThrough)

      if (stepThrough) yield take(NEXT_CAMERA)
    }
  } else {
    console.warn('DEBUG', `Taking single screenshot, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

    const { deviceCount } = yield select(setupNetworkClosetSelector)

    const snapshotIndex = (lambdaNumber - 1) * 5 + index
    const screenshotOpts = {
      fileName: `network_closet_${snapshotIndex}`,
      name: deviceCount > 0 ? `${networkCloset.name} (Devices 1–${deviceCount} of ${deviceCount})` : networkCloset.name,
      index: snapshotIndex
    }
    yield call(takeScreenshot, screenshotOpts, stepThrough)

    if (stepThrough) yield take(NEXT_CAMERA)
  }

  yield flush(global.networkClosetListChannel)
}

function * doWiredDevice(wiredDevice, index, stepThrough, map, lambdaNumber) {
  const timeBeforeDeviceIsReady = Date.now()

  yield call(setupWiredDevice, wiredDevice, map.mapType !== 'blank' && index === 0)

  yield call(waitForMapOnceRace, map, timeBeforeDeviceIsReady)
  yield call(waitForTileCountRace, map)
  yield call(waitForTilesToLoadRace, map)

  console.warn('DEBUG', `Ready to take snapshot for wired device ${wiredDevice.id}, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

  if (index === 0) yield call(delay, 500)

  const snapshotIndex = (lambdaNumber - 1) * 5 + index
  yield call(takeScreenshot, { fileName: `${wiredDevice.type.toLowerCase().replace(/(\s\/\s)|\s+/, '_')}_${snapshotIndex}`, name: wiredDevice.name, index: snapshotIndex }, stepThrough)

  if (stepThrough) yield take(NEXT_CAMERA)
}

function * doDoor(door, snapshotIndex, controllerName, stepThrough, map) {
  const timeBeforeDeviceIsReady = Date.now()

  yield call(setupDoor, door)

  yield call(waitForMapOnceRace, map, timeBeforeDeviceIsReady)
  yield call(waitForTileCountRace, map)
  yield call(waitForTilesToLoadRace, map)

  console.warn('DEBUG', `Ready to take snapshot for door ${door.id}, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

  yield call(takeScreenshot, { fileName: `door_${snapshotIndex}`, name: `${controllerName}: ${door.name}`, index: snapshotIndex }, stepThrough)

  if (stepThrough) yield take(NEXT_CAMERA)
}

const getDigits = num => Math.max(Math.floor(Math.log10(num)), 0) + 1

function * doExpansionController(expansionController, doors, snapshotIndex, controllerName, stepThrough, map, totalDigits) {
  const timeBeforeDeviceIsReady = Date.now()
  const { id, name, doorIds } = expansionController

  yield call(setupDoorController, expansionController, false)

  yield call(waitForMapOnceRace, map, timeBeforeDeviceIsReady)
  yield call(waitForTileCountRace, map)
  yield call(waitForTilesToLoadRace, map)

  console.warn('DEBUG', `Ready to take snapshot for expansion controller ${id}, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

  yield call(takeScreenshot, { fileName: `expansion_controller_${snapshotIndex}`, name: `${controllerName}: ${name}`, index: snapshotIndex }, stepThrough)

  if (stepThrough) yield take(NEXT_CAMERA)

  const doorDigits = getDigits(doorIds.length)

  for (let doorIdx = 0; doorIdx < doorIds.length; doorIdx++) {
    const door = doors[doorIds[doorIdx]]
    const doorSnapshotIndex = snapshotIndex + (doorIdx + 1) * 1.0 / Math.pow(10, totalDigits + doorDigits)

    yield call(doDoor, door, doorSnapshotIndex, name, stepThrough, map)
  }
}

function * doDoorController(doorController, doorControllers, doors, index, stepThrough, map, lambdaNumber) {
  const timeBeforeDeviceIsReady = Date.now()
  const { id, name, doorIds, expansionIds } = doorController

  yield call(setupDoorController, doorController, map.mapType !== 'blank' && index === 0)

  yield call(waitForMapOnceRace, map, timeBeforeDeviceIsReady)
  yield call(waitForTileCountRace, map)
  yield call(waitForTilesToLoadRace, map)

  console.warn('DEBUG', `Ready to take snapshot for door controller ${id}, took ${Date.now() - timeBeforeDeviceIsReady}ms`)

  if (index === 0) yield call(delay, 500)

  const snapshotIndex = (lambdaNumber - 1) * 5 + index
  yield call(takeScreenshot, { fileName: `door_controller_${snapshotIndex}`, name, index: snapshotIndex }, stepThrough)

  if (stepThrough) yield take(NEXT_CAMERA)

  const doorDigits = getDigits(doorIds.length)

  for (let doorIdx = 0; doorIdx < doorIds.length; doorIdx++) {
    const door = doors[doorIds[doorIdx]]
    const doorSnapshotIndex = snapshotIndex + (doorIdx + 1) * 1.0 / Math.pow(10, doorDigits)

    yield call(doDoor, door, doorSnapshotIndex, name, stepThrough, map)
  }

  const expansionDigits = getDigits(expansionIds.length)

  for (let expansionIdx = 0; expansionIdx < expansionIds.length; expansionIdx++) {
    const expansion = doorControllers[expansionIds[expansionIdx]]
    const expansionSnapshotIndex = snapshotIndex + doorIds.length * 1.0 / Math.pow(10, doorDigits) + (expansionIdx + 1) * 1.0 / Math.pow(10, doorDigits + expansionDigits)

    yield call(doExpansionController, expansion, doors, expansionSnapshotIndex, name, stepThrough, map, doorDigits + expansionDigits)
  }
}

function * doDevices(deviceIds, stepThrough, map, lambdaNumber) {
  const { doorControllers, doors, networkClosets, wiredDevices, currentEntity: { type, id } } = yield select(exportDoDevicesSelector)
  let firstCloset = true

  console.warn('DEBUG', `Starting to process devices: ${deviceIds.map(device => `${device.type} ${device.id}`)}`)

  for (let [index, device] of deviceIds.entries()) {
    if (device.type === 'doorController') yield call(doDoorController, doorControllers[device.id], doorControllers, doors, index, stepThrough, map, lambdaNumber)
    else if (device.type === 'wiredDevice') yield call(doWiredDevice, wiredDevices[device.id], index, stepThrough, map, lambdaNumber)
    else {
      if (firstCloset) {
        console.warn('DEBUG', 'Hiding camera views for first network closet')
        yield put(updateUiAction({ cameraViewsChoice: 'None' }))
      }

      yield call(doNetworkCloset, networkClosets[device.id], index, stepThrough, map, lambdaNumber, firstCloset && type === 'networkCloset' && device.id !== id)
      if (firstCloset) firstCloset = false
    }
  }
}

function * prepareForSnapshot(map, cameras) {
  try {
    console.warn('DEBUG', 'Preparing snapshot')
    yield call(waitForMapOnceRace, map, Date.now())

    yield call(waitForTileCountRace, map)
    yield call(waitForTilesToLoadRace, map)
    yield call(waitForCameraModelImage, cameras)
  } catch (err) {
    yield put(notifyHoneybadgerAction(err, {}))
  }
}

function * initializeOverview(map) {
  try {
    yield put(updateCurrentEntityAction({ type: 'map' }))
    const bounds = yield call(zoomToAllDevices)
    yield call(waitForMapOnceRace, map, Date.now())

    yield call(waitForTileCountRace, map)
    yield call(waitForTilesToLoadRace, map)

    return bounds
  } catch (err) {
    yield put(notifyHoneybadgerAction(err, {}))
  }
}

function * renderCollection({ map, cameras, stepThrough, cameraIds, deviceIds, overview, lambdaNumber }) {
  // OVERVIEW
  if (overview) {
    const bounds = yield call(initializeOverview, map)

    const overviewOpts = {
      fileName: 'overview',
      name: 'Overview',
      index: 0,
      shrinkWindow: true,
      clip: getScreenshotClipFromBounds(bounds, map.ref)
    }
    yield call(takeScreenshot, overviewOpts, stepThrough)

    if (stepThrough) yield take(NEXT_CAMERA)
  }

  yield put.resolve(updateExportTypeAndResizeMapAction(''))

  // CAMERAS
  if (cameraIds) yield call(doCameras, cameras, stepThrough, cameraIds, map, lambdaNumber)

  // NETWORK CLOSETS
  if (deviceIds) yield call(doDevices, deviceIds, stepThrough, map, lambdaNumber)

  yield call(exit)
}

function * renderSingle({ map, cameras, stepThrough }) {
  yield call(prepareForSnapshot, map, cameras)
  console.warn('DEBUG', 'Snapshot prepared')
  yield call(takeScreenshot, { fileName: 'snapshot', name: 'snapshot', index: 0 }, stepThrough)
  yield call(exit)
}

function * startExportProcess(stepThrough, cameraIds, deviceIds, overview, lambdaNumber) {
  yield take(PROJECT_EXPANDED)
  const { cameras, exportType, map } = yield select(startExportProcessSelector)

  yield call(waitForFloorPlans, map.floorPlans || [])
  switch (exportType) {
    case 'Single':
      return yield call(renderSingle, { map, cameras, stepThrough })
    case 'Collection':
      return yield call(renderCollection, { map, cameras, stepThrough, cameraIds, deviceIds, overview, lambdaNumber })
    default:
      throw new Error('Invalid exportType specified')
  }
}

function * startExportTimer() {
  yield call(delay, 30000) // 30 seconds
  window.handlers.exit('Export timer reached')
}

export function * setupExport({ project, cameraDataFilename, stepThrough }) {
  try {
    yield spawn(startExportTimer)
    yield call(getAndApplyCameraData, cameraDataFilename)
    yield put(applyProjectsAction([project]))

    global.floorPlanReadyChannel = yield actionChannel(FLOOR_PLAN_READY, buffers.expanding(1))
    global.networkClosetListChannel = yield actionChannel(SET_NETWORK_CLOSET_LIST_PAGES, buffers.expanding(1))

    yield spawn(startExportProcess, stepThrough, project.cameraIds, project.deviceIds, project.overview, project.lambdaNumber)

    yield put.resolve(expandProjectAction(project))
    yield put(projectExpandedAction())
  } catch (err) {
    yield put(notifyHoneybadgerAction(err, { type: 'SETUP_EXPORT', project }))
  }
}

export function * watchSetupExport() {
  yield * takeEvery(SETUP_EXPORT, setupExport)
}



// WEBPACK FOOTER //
// ./src/sagas/setupExportSaga/index.js